import './App.css';
import { type IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { use } from 'react';
import { RouterProvider } from 'react-router';

import { appRouter } from './appRouter/appRouter';
import { theme } from './theme/theme.js';
import { queryClient, trpc, trpcClient } from './trpc';
import { BackendConfigContext } from './utils/BackendInfrastructure/BackendConfigContext';
import { loginRequest } from './utils/loginRequest.js';

const TrpcProvider = trpc.Provider;

type AppProps = {
  readonly msalInstance: IPublicClientApplication;
};

const getIdToken = (instance: IPublicClientApplication) => async () => {
  const account = instance.getActiveAccount();

  if (!account) {
    throw new Error('No active account found. Please log in first.');
  }

  try {
    return (
      await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      })
    ).idToken;
  } catch (error) {
    console.error('AuthError: ', error);
    await instance.loginRedirect(loginRequest);
    return (
      await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      })
    ).idToken;
  }
};

const App = ({ msalInstance }: AppProps) => {
  const backendConfig = use(BackendConfigContext);
  const trpcClientInstance = trpcClient(getIdToken(msalInstance), backendConfig.BackendUrl);
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <ScopedCssBaseline>
          <TrpcProvider client={trpcClientInstance} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={appRouter} />
            </QueryClientProvider>
          </TrpcProvider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </MsalProvider>
  );
};

export default App;
